import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { z } from 'zod';

import { config } from '../../../../config/config';
import { getClient } from '../../../main/graphql';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { Field } from '../../../shared/components/field/field';
import type { IOnFieldChangeParameters } from '../../../shared/components/form-builder/form-builder';
import { DisplayType } from '../../../shared/components/form-builder/form-builder';
import { FormFlow } from '../../../shared/components/form-flow/form-flow';
import type { IFlowState } from '../../../shared/components/form-flow/form-flow';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { MessageState } from '../../../shared/components/message-state/message-state';
import { useRegistrationAge } from '../../../shared/hooks/registration-age';
import { getCookie } from '../../../shared/hooks/use-cookie';
import { selectLocale } from '../../../shared/store/locale.store';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { exists } from '../../../shared/utils/filters';
import { userPhonePrefixList } from '../../../shared/utils/phone-prefix';
import { slugify } from '../../../shared/utils/strings';
import { useSetCookieFromParams } from '../../hooks/use-set-cookie-from-params';
import type {
    IRegisterUserMutation,
    IRegisterUserMutationVariables,
} from '../../queries/register-user.graphql';
import {
    RegisterUserDocument,
    useGetDocumentLatestVersionByTypeQuery,
} from '../../queries/register-user.graphql';

import styles from './registration-flow.module.scss';
import { registrationStepOneSchema } from './registration-step-1.schema';
import { registrationStepTwoSchema } from './registration-step-2.schema';
import { registrationStepThreeSchema } from './registration-step-3.schema';
import {
    ADDRESS_COUNTRY_FIELD,
    BANNER_COMPLETE_REGISTRATION,
    BIRTH_DATE_FIELD,
    CONFIRM_PASSWORD_FIELD,
    EMAIL_FIELD,
    FAMILY_NAME_FIELD,
    GIVEN_NAME_FIELD,
    MARKETING_FIELD,
    PASSWORD_FIELD,
    POSTAL_CODE_FIELD,
    STREET_ADDRESS_FIELD,
    TEL_FIELD,
    TEL_PREFIX_FIELD,
    TERMS_FIELD,
    USERNAME_FIELD,
    TAX_ID_FIELD,
    LOCALITY_FIELD,
    REGION_FIELD,
    MARKETING_CAMPAIGN,
} from './registration.constants';
import type { registrationSchema } from './registration.schema';

const style = createStyleHelper(styles, 'registration-flow');

const UTM_SOURCE_STORAGE_KEY = 'utm_source';
const UTM_MEDIUM_STORAGE_KEY = 'utm_medium';
const UTM_CAMPAIGN_STORAGE_KEY = 'utm_campaign';
const UTM_TERM_STORAGE_KEY = 'utm_term';
const UTM_CONTENT_STORAGE_KEY = 'utm_content';
const UTM_AGENT_STORAGE_KEY = 'utm_agent';
const UTM_PAYLOAD_STORAGE_KEY = 'utm_payload';

export interface IRegistrationFlowFormProps {
    className?: string;
}

export const RegistrationFlow: FunctionComponent<IRegistrationFlowFormProps> = ({ className }) => {
    const registrationMinimumAge = useRegistrationAge();
    const locale = useSelector(selectLocale);

    useSetCookieFromParams(UTM_SOURCE_STORAGE_KEY);
    useSetCookieFromParams(UTM_MEDIUM_STORAGE_KEY);
    useSetCookieFromParams(UTM_CAMPAIGN_STORAGE_KEY);
    useSetCookieFromParams(UTM_TERM_STORAGE_KEY);
    useSetCookieFromParams(UTM_CONTENT_STORAGE_KEY);
    useSetCookieFromParams(UTM_AGENT_STORAGE_KEY);
    useSetCookieFromParams(UTM_PAYLOAD_STORAGE_KEY);

    const schemaStepOne = useMemo(() => {
        return registrationStepOneSchema();
    }, []);

    const schemaStepTwo = useMemo(() => {
        return registrationStepTwoSchema(registrationMinimumAge);
    }, [registrationMinimumAge]);

    const schemaStepThree = useMemo(() => {
        return registrationStepThreeSchema();
    }, []);

    const [{ data: termsData }] = useGetDocumentLatestVersionByTypeQuery({
        variables: {
            xTenantID: config.TENANT_ID,
            locale: locale,
            skinId: config.SKIN_ID,
            slug: 'general-terms-of-service',
        },
    });

    const [{ data: privacyData }] = useGetDocumentLatestVersionByTypeQuery({
        variables: {
            xTenantID: config.TENANT_ID,
            locale: locale,
            skinId: config.SKIN_ID,
            slug: 'privacy-policy',
        },
    });

    const currentTermsVersion = termsData?.getDocumentBySlug?.version ?? '';
    const currentPolicyVersion = privacyData?.getDocumentBySlug?.version ?? '';

    const steps = useMemo(() => {
        return [
            {
                name: 'step-1',
                schema: schemaStepOne,
                onBeforeEnterStep: (formDataProps?: Record<string, unknown>) => formDataProps,
                onFieldChange: ({ name, values, setValue }: IOnFieldChangeParameters) => {
                    if (name === ADDRESS_COUNTRY_FIELD.name) {
                        const result = userPhonePrefixList.find((item) => {
                            return item?.countryCode === values.addrCountryCode;
                        });
                        setValue(TEL_PREFIX_FIELD.name, result?.value);
                    }
                },
                components: ({ isLoading, formState }: IFlowState) =>
                    [
                        USERNAME_FIELD,
                        GIVEN_NAME_FIELD,
                        FAMILY_NAME_FIELD,
                        EMAIL_FIELD,
                        PASSWORD_FIELD,
                        CONFIRM_PASSWORD_FIELD,
                        // TEL_PREFIX_FIELD,
                        TEL_FIELD,
                        MARKETING_FIELD,

                        formState?.validations?.errors?.root?.serverError
                            ? {
                                  name: 'error',
                                  type: DisplayType.COMPONENT,
                                  component: Field,
                                  props: {
                                      className: style('field', ['error']),
                                      hasError: !!formState?.validations?.errors?.root?.serverError,
                                      children: (
                                          <FormattedMessage
                                              id={`registration.step-one.${slugify(
                                                  formState.validations.errors.root.serverError
                                                      ?.message,
                                              )}.error`}
                                              defaultMessage={
                                                  formState.validations.errors.root.serverError
                                                      ?.message
                                              }
                                          />
                                      ),
                                  },
                              }
                            : null,
                        {
                            name: 'submit',
                            type: DisplayType.COMPONENT,
                            component: Button,
                            props: {
                                className: style('button'),
                                children: (
                                    <FormattedMessage
                                        id="registration.step-two.submit.button"
                                        defaultMessage="Next"
                                    />
                                ),
                                disabled: isLoading,
                                loading: isLoading,
                                type: 'submit',
                                size: ButtonSize.MEDIUM,
                            },
                        },
                    ] as const,
            },
            {
                name: 'step-2',
                schema: schemaStepTwo,
                onFieldChange: ({ name, values, setValue }: IOnFieldChangeParameters) => {
                    if (name === ADDRESS_COUNTRY_FIELD.name) {
                        const result = userPhonePrefixList.find((item) => {
                            return item?.countryCode === values.addrCountryCode;
                        });
                        setValue(TEL_PREFIX_FIELD.name, result?.value);
                    }
                },
                components: ({ isLoading, formState }: IFlowState) =>
                    [
                        BIRTH_DATE_FIELD,
                        STREET_ADDRESS_FIELD,
                        LOCALITY_FIELD,
                        POSTAL_CODE_FIELD,
                        REGION_FIELD,
                        // ADDRESS_COUNTRY_FIELD,
                        TAX_ID_FIELD,

                        formState?.validations?.errors?.root?.serverError
                            ? {
                                  name: 'error',
                                  type: DisplayType.COMPONENT,
                                  component: Field,
                                  props: {
                                      className: style('field', ['error']),
                                      hasError: !!formState?.validations?.errors?.root?.serverError,
                                      children: (
                                          <FormattedMessage
                                              id={`registration.step-one.${slugify(
                                                  formState.validations.errors.root.serverError
                                                      ?.message,
                                              )}.error`}
                                              defaultMessage={
                                                  formState.validations.errors.root.serverError
                                                      ?.message
                                              }
                                          />
                                      ),
                                  },
                              }
                            : null,
                        {
                            name: 'submit',
                            type: DisplayType.COMPONENT,
                            component: Button,
                            props: {
                                className: style('button'),
                                children: (
                                    <FormattedMessage
                                        id="registration.step-two.submit.button"
                                        defaultMessage="Next"
                                    />
                                ),
                                disabled: isLoading,
                                loading: isLoading,
                                type: 'submit',
                                size: ButtonSize.MEDIUM,
                            },
                        },
                    ] as const,
            },
            {
                name: 'step-3',
                schema: schemaStepThree,
                onFieldChange: ({ name, values, setValue }: IOnFieldChangeParameters) => {
                    if (name === ADDRESS_COUNTRY_FIELD.name) {
                        const result = userPhonePrefixList.find((item) => {
                            return item?.countryCode === values.addrCountryCode;
                        });
                        setValue(TEL_PREFIX_FIELD.name, result?.value);
                    }
                },
                components: ({ isLoading, formState }: IFlowState) =>
                    [
                        MARKETING_CAMPAIGN,
                        TERMS_FIELD,

                        formState?.validations?.errors?.root?.serverError
                            ? {
                                  name: 'error',
                                  type: DisplayType.COMPONENT,
                                  component: Field,
                                  props: {
                                      className: style('field', ['error']),
                                      hasError: !!formState?.validations?.errors?.root?.serverError,
                                      children: (
                                          <FormattedMessage
                                              id={`registration.step-one.${slugify(
                                                  formState.validations.errors.root.serverError
                                                      ?.message,
                                              )}.error`}
                                              defaultMessage={
                                                  formState.validations.errors.root.serverError
                                                      ?.message
                                              }
                                          />
                                      ),
                                  },
                              }
                            : null,
                        {
                            name: 'submit',
                            type: DisplayType.COMPONENT,
                            component: Button,
                            props: {
                                className: style('button'),
                                children: (
                                    <FormattedMessage
                                        id="registration.step-one.submit.button"
                                        defaultMessage="Join Crab Sports"
                                    />
                                ),
                                disabled: isLoading,
                                loading: isLoading,
                                type: 'submit',
                                size: ButtonSize.MEDIUM,
                            },
                        },
                    ] as const,
                onBeforeLeaveStep: async (
                    form: z.infer<ReturnType<typeof registrationSchema>> | undefined,
                ) => {
                    const responseData = await getClient()
                        .mutation<IRegisterUserMutation, IRegisterUserMutationVariables>(
                            RegisterUserDocument,
                            {
                                xTenantID: config.TENANT_ID,
                                model: {
                                    addrPostalCode: form?.addrPostalCode ?? '',
                                    addrStreetAddress: form?.addrStreetAddress ?? '',
                                    addrRegion: form?.addrRegion ?? '',
                                    addrCountryCode: 'US',
                                    addrLocality: form?.addrLocality ?? '',
                                    email: form?.email ?? '',
                                    givenName: form?.givenName ?? '',
                                    familyName: form?.familyName ?? '',
                                    telephone: `+1${form?.telephone}`,
                                    password: form?.password ?? '',
                                    username: form?.username ?? '',
                                    birthDate: form?.birthDate ?? '',
                                    taxId: form?.taxId ?? '',
                                    gender: form?.gender ?? '',
                                    termsAndConditionsVersion: currentTermsVersion,
                                    privacyPolicyVersion: currentPolicyVersion,
                                    optIn: form?.promo ?? false,
                                    utm_source: getCookie(UTM_SOURCE_STORAGE_KEY) ?? '',
                                    utm_medium: getCookie(UTM_MEDIUM_STORAGE_KEY) ?? '',
                                    utm_campaign: form?.marketing_campaign ?? '',
                                    utm_term: getCookie(UTM_TERM_STORAGE_KEY) ?? '',
                                    utm_content: getCookie(UTM_CONTENT_STORAGE_KEY) ?? '',
                                    utm_agent: getCookie(UTM_AGENT_STORAGE_KEY) ?? '',
                                    utm_payload: getCookie(UTM_PAYLOAD_STORAGE_KEY) ?? '',
                                },
                            },
                        )
                        .toPromise();

                    if (responseData?.data?.registerUser?.data && responseData?.operation) {
                        window.gtag('event', 'registration', {
                            event_category: 'User Actions',
                            user_email: responseData?.operation?.variables?.model?.email ?? '',
                            user_username:
                                responseData?.operation?.variables?.model?.username ?? '',
                        });
                        window.fbq('trackCustom', 'Registration', {
                            event_category: 'User Actions',
                            user_email: responseData?.operation?.variables?.model?.email ?? '',
                            user_username:
                                responseData?.operation?.variables?.model?.username ?? '',
                        });
                        // window.rdt('track', 'SignUp');
                    }
                    return {
                        errors: responseData?.data?.registerUser?.error
                            ?.filter(exists)
                            .map((error) => {
                                const isFieldError =
                                    error.__typename === 'FieldError' && error.fieldName !== '$';

                                return {
                                    type: isFieldError ? ('field' as const) : ('form' as const),
                                    fieldName: isFieldError ? error.fieldName : undefined,
                                    messages:
                                        error.__typename === 'FieldError'
                                            ? error.kind.filter(exists)
                                            : undefined,
                                };
                            }),
                    };
                },
            },
            {
                name: 'complete',
                components(state: IFlowState) {
                    const errors = state.additionalData.errors;

                    return [
                        {
                            name: 'complete',
                            type: DisplayType.COMPONENT,
                            component: MessageState,
                            props: {
                                errors,
                                isLoading: state.isLoading,
                                className: style('message'),
                                title: errors?.length
                                    ? 'registration.error.title'
                                    : 'registration.complete-registration.title',
                                message: errors?.length
                                    ? 'registration.error.message'
                                    : 'registration.complete-registration.message',
                                actionText: 'registration.complete-registration.button',
                                actionHref: '/',
                            },
                        },
                        BANNER_COMPLETE_REGISTRATION,
                    ];
                },
            },
        ] as const;
    }, [schemaStepOne, schemaStepTwo, schemaStepThree, currentTermsVersion]);

    return (
        <FormFlow
            className={createClassName([className, style()])}
            steps={steps}
            page={'registration'}
        />
    );
};
